import React from 'react';

import classNames from 'classnames';

import { escalationReason, localDate, statusChipClass, STATUS_APPROVED, STATUS_REJECTED } from './changeRequestFormat';

const LEVEL_STATUS_LABEL = {
    approved: () => $t('Approved'),
    rejected: () => $t('Rejected'),
    pending: () => $t('Pending'),
    not_due: () => $t('Not yet due'),
    withdrawn: () => $t('Withdrawn'),
    not_reached: () => $t('Not reached'),
};

const INACTIVE_STATUSES = ['not_due', 'withdrawn', 'not_reached'];

const chipClassFor = (status) => {
    if (status === 'approved') {
        return statusChipClass(STATUS_APPROVED);
    }

    if (INACTIVE_STATUSES.includes(status)) {
        return 'bc-chip bc-chip--grey';
    }

    return status === 'rejected' ? statusChipClass(STATUS_REJECTED) : statusChipClass(null);
};

/**
 * Level 1 pending carries no escalation reason, and a rejected level with no comments
 * carries nothing at all — so the detail line is genuinely absent on some levels, and
 * an empty `bc-sub` div still occupies its margin.
 *
 * A chain that marks the viewer's own level (`is_viewer` is a boolean, not null) has
 * no escalation values, so its pending levels say whose turn it is instead.
 */
const detailFor = (level) => {
    if (level.is_viewer) {
        return $t('You are approving this level');
    }

    if (INACTIVE_STATUSES.includes(level.status) || (level.status === 'pending' && marksViewer(level))) {
        return null;
    }

    return (
        (level.status === 'pending'
            ? escalationReason(level)
            : [level.approved_at_utc ? localDate(level.approved_at_utc) : null, level.comments ? `“${level.comments}”` : null].filter(Boolean).join(' · ')) ||
        null
    );
};

/** Whether the chain marks the viewer's own level — only a chain that does colours the badges by turn. */
const marksViewer = (level) => typeof level.is_viewer === 'boolean';

/**
 * A finished level's badge is green. On a chain that marks the viewer's level, the
 * viewer's turn is green too, a rejection is red and a level that is not live is grey.
 */
const badgeClassFor = (level) =>
    classNames('bc-level__badge', {
        'bc-level__badge--done': level.status === 'approved' || (marksViewer(level) && level.is_viewer),
        'bc-level__badge--rejected': marksViewer(level) && level.status === 'rejected',
        'bc-level__badge--muted': marksViewer(level) && INACTIVE_STATUSES.includes(level.status),
    });

/**
 * Approval progress by escalation level. The levels come from the chain the request
 * itself carries, so a historic request keeps reading "1 of 2" after the rule has
 * grown a third level; only the bracketed escalation value reads current config.
 */
export default function ApprovalStepper({ approval, emptyLabel = null }) {
    const levels = approval?.levels ?? [];

    if (levels.length === 0) {
        return <div className="bc-sub">{emptyLabel || $t('No approval levels are configured for this change.')}</div>;
    }

    return (
        <div className="bc-chain">
            {levels.map((level) => {
                const detail = detailFor(level);

                return (
                    <div className="bc-level tw-items-start" key={level.level}>
                        <span className={badgeClassFor(level)}>{$t('Level {{level}}', { level: level.level })}</span>
                        <div className="tw-min-w-0">
                            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                                <span className="tw-font-medium">{level.name}</span>
                                <span className={chipClassFor(level.status)}>{(LEVEL_STATUS_LABEL[level.status] || LEVEL_STATUS_LABEL.pending)()}</span>
                            </div>
                            {detail && <div className="bc-sub">{detail}</div>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
